import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { contactsApi } from "@/lib/api";
import { toast } from "sonner";

const empty = { full_name: "", company: "", role: "", email: "", phone: "", preferred_channel: "email",
  last_contacted: "", next_followup: "", notes: "" };

export default function ContactDialog({ open, setOpen, contact, onSaved }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(contact ? { ...empty, ...contact } : empty); }, [contact, open]);
  const set = (k,v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.full_name.trim()) return toast.error("Full name is required");
    setSaving(true);
    try {
      if (contact?.id) {
        await contactsApi.update(contact.id, form);
        toast.success("Contact updated");
      } else {
        const r = await contactsApi.create(form);
        if (r.data.duplicate) toast.info("A matching contact already existed and was returned.");
        else toast.success("Contact created");
      }
      onSaved?.();
    } catch(e){ toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#151A23] border-white/10 text-white max-w-lg" data-testid="contact-dialog">
        <DialogHeader><DialogTitle className="font-display">{contact?.id ? "Edit contact" : "New contact"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="col-span-2"><Label className="text-white/60 text-xs uppercase tracking-wider">Full name</Label><Input data-testid="contact-name-input" value={form.full_name} onChange={e=>set("full_name", e.target.value)} className="mt-1.5 bg-white/5 border-white/10"/></div>
          <div><Label className="text-white/60 text-xs uppercase tracking-wider">Company</Label><Input value={form.company || ""} onChange={e=>set("company", e.target.value)} className="mt-1.5 bg-white/5 border-white/10"/></div>
          <div><Label className="text-white/60 text-xs uppercase tracking-wider">Role</Label><Input value={form.role || ""} onChange={e=>set("role", e.target.value)} className="mt-1.5 bg-white/5 border-white/10"/></div>
          <div><Label className="text-white/60 text-xs uppercase tracking-wider">Email</Label><Input type="email" value={form.email || ""} onChange={e=>set("email", e.target.value)} className="mt-1.5 bg-white/5 border-white/10"/></div>
          <div><Label className="text-white/60 text-xs uppercase tracking-wider">Phone</Label><Input value={form.phone || ""} onChange={e=>set("phone", e.target.value)} className="mt-1.5 bg-white/5 border-white/10"/></div>
          <div>
            <Label className="text-white/60 text-xs uppercase tracking-wider">Preferred channel</Label>
            <Select value={form.preferred_channel} onValueChange={v=>set("preferred_channel", v)}><SelectTrigger className="mt-1.5 bg-white/5 border-white/10"><SelectValue/></SelectTrigger>
              <SelectContent className="bg-[#151A23] border-white/10 text-white">{["email","phone","in-person","other"].map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-white/60 text-xs uppercase tracking-wider">Next follow-up</Label><Input type="date" value={form.next_followup?.slice(0,10) || ""} onChange={e=>set("next_followup", e.target.value)} className="mt-1.5 bg-white/5 border-white/10"/></div>
          <div className="col-span-2"><Label className="text-white/60 text-xs uppercase tracking-wider">Notes</Label><Textarea rows={3} value={form.notes || ""} onChange={e=>set("notes", e.target.value)} className="mt-1.5 bg-white/5 border-white/10"/></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={()=>setOpen(false)} className="text-white/70 hover:text-white hover:bg-white/10">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="contact-save-btn" className="rounded-full bg-white text-black hover:bg-white/90">{saving?"Saving…":"Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
